import { Component, DestroyRef, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { AuthService } from '../shared/services/auth.service'
import { SessionExpiredServiceService } from '../shared/services/session-expired-service.service'
import { environment } from 'src/environments/environment'

interface LandingCard {
  title: string
  description: string
  link?: string
  routeCommands?: string[]
  icon: string
}

/** Consumption Layer: `landingPageLinks.dashboard` is an in-app path (e.g. pages/reports/records) or an external URL. */
function buildConsumptionLayerLandingCard(): LandingCard {
  const dashboard = environment.landingPageLinks.dashboard?.trim() ?? ''
  const base: LandingCard = {
    title: 'FORWARD Consumption Layer',
    description:
      'Provides portfolio level forecast insights across various brands and therapy areas',
    icon: 'assets/landing/icon-dashboard.svg',
  }
  if (!dashboard) {
    return base
  }
  if (/^https?:\/\//i.test(dashboard)) {
    return { ...base, link: dashboard }
  }
  return {
    ...base,
    routeCommands: dashboard.split('/').filter((s) => s.length > 0),
  }
}

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent implements OnInit {
  userName: string = ''
  isAuthRefreshing = false
  authRefreshSucceeded = false
  /** Full-screen overlay only when there is no persisted session (missing / cleared); not on routine visits. */
  showAuthenticatingOverlay = false
  readonly governanceLink = environment.landingPageLinks.governance

  readonly cards: LandingCard[] = [
    {
      title: 'FORWARD Governance',
      description:
        'Provides centralized governance and administration of forecast cycles across teams, including timelines, milestones, and access controls',
      link: environment.landingPageLinks.governance,
      icon: 'assets/landing/icon-governance.svg',
    },
    {
      title: 'FORWARD Projection Engine',
      description:
        'Enables data-driven projections using advanced statistical and ML-based models',
      link: environment.landingPageLinks.driverless,
      icon: 'assets/landing/icon-driverless.svg',
    },
    buildConsumptionLayerLandingCard(),
    {
      title: 'FORWARD.AI',
      description:
        'Agent-enabled forecasting platform that standardizes forecast creation, analysis, and explanation through embedded AI',
      link: environment.landingPageLinks.forwardAi,
      icon: 'assets/landing/icon-forward-ai.svg',
    },
  ]

  constructor(
    private router: Router,
    private authService: AuthService,
    private sessionExpiredService: SessionExpiredServiceService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    const user: any = this.authService.getUserDetails()
    this.userName = user?.UserName || user?.DisplayName || 'User'
    this.refreshAuthFromServer()
  }

  cardHasTarget(card: LandingCard): boolean {
    const hasLink = !!(card.link && card.link.trim().length > 0)
    const hasRoute = !!(card.routeCommands && card.routeCommands.length > 0)
    return hasLink || hasRoute
  }

  needsAuthRefreshForCard(card: LandingCard): boolean {
    if (card.routeCommands && card.routeCommands.length > 0) {
      return true
    }
    const link = card.link ?? ''
    return link === this.governanceLink || link === 'welcome'
  }

  isCardButtonDisabled(card: LandingCard): boolean {
    if (!this.cardHasTarget(card)) {
      return true
    }
    if (this.needsAuthRefreshForCard(card)) {
      return this.isAuthRefreshing || !this.authRefreshSucceeded
    }
    return false
  }

  navigateCard(card: LandingCard): void {
    if (!this.cardHasTarget(card) || this.isCardButtonDisabled(card)) {
      return
    }

    if (card.routeCommands && card.routeCommands.length > 0) {
      this.router.navigate(card.routeCommands)
      return
    }

    const link = card.link ?? ''
    if (link === this.governanceLink || link === 'welcome') {
      this.router.navigate(['pages'])
      return
    }

    if (link.startsWith('http://') || link.startsWith('https://')) {
      window.open(link, '_blank', 'noopener,noreferrer')
      return
    }

    if (link) {
      this.router.navigate([link])
    }
  }

  private refreshAuthFromServer(): void {
    this.isAuthRefreshing = true
    this.authRefreshSucceeded = false
    this.showAuthenticatingOverlay = !this.authService.isLoggedIn()
    this.authService
      .getLoggedInUserDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.applyLoggedInUserDetails(response)
          this.isAuthRefreshing = false
          this.authRefreshSucceeded = true
          this.showAuthenticatingOverlay = false
        },
        error: (errorResponse) => {
          this.isAuthRefreshing = false
          this.authRefreshSucceeded = false
          this.showAuthenticatingOverlay = false
          this.authService.logout()
          this.sessionExpiredService.show()
          console.error(errorResponse)
        },
      })
  }

  private applyLoggedInUserDetails(response: any): void {
    this.authService.login(response.Response.LoggedUserDetails)
    const currentRole = this.authService.getUserRole()
    if (currentRole != null && currentRole != undefined && currentRole != '') {
      this.authService.setUserRole(currentRole)
    } else {
      this.authService.setUserRole(response.Response.LoggedUserDetails.Roles[0])
    }
    response.Response.LoggedUserDetails.Roles.forEach((element: string) => {
      this.authService.setUserForecasts(element, response.Response.LoggedUserDetails.RoleDetails[element])
    })
  }
}
